// apps/client/src/hooks/useCompanyDataYearRange.ts
// Fetches the earliest and latest years of commission data for a company
// Used to dynamically populate year dropdowns on dashboard filters
// MIGRATED (2026-07-10): Fetches through the shared dashboard query cache (useDashboardQuery).

import { useDashboardQuery } from './useDashboardQuery';

interface UseCompanyDataYearRangeParams {
  companyId: string | undefined;
}

interface UseCompanyDataYearRangeReturn {
  earliestYear: number;
  latestYear: number;
  earliestMonth: string;  // YYYY-MM format for ALL period selector
  loading: boolean;
  error: string | null;
}

interface YearRangeResponse {
  earliestYear: number;
  latestYear: number;
  earliestMonth: string;  // YYYY-MM format
}

/**
 * Hook to fetch the year range of commission data for a company
 *
 * Returns:
 * - earliestYear: First year with commission data
 * - latestYear: Most recent year with data (or current year)
 * - loading: Whether the query is in progress
 * - error: Error message if query failed
 *
 * Falls back to a 5-year window if company has no data or query fails.
 */
export function useCompanyDataYearRange({
  companyId,
}: UseCompanyDataYearRangeParams): UseCompanyDataYearRangeReturn {
  const currentYear = new Date().getFullYear();
  const defaultEarliestYear = currentYear - 4;
  const defaultEarliestMonth = `${defaultEarliestYear}-01`;

  const { data, loading, error } = useDashboardQuery<YearRangeResponse>(
    'getCompanyDataYearRange',
    { companyId: companyId ?? '' }
  );

  // Fall back to the default 5-year window when the company has no data or the
  // query fails/has not resolved (original hook kept its default state on error).
  const earliestYear = data?.earliestYear ?? defaultEarliestYear;
  const latestYear = data?.latestYear ?? currentYear;
  const earliestMonth = data?.earliestMonth ?? defaultEarliestMonth;

  return {
    earliestYear,
    latestYear,
    earliestMonth,
    loading,
    error,
  };
}
